import * as marketing from "../lib/api/marketing";
import logger from "../lib/sentryLogger";
import React, {useState, createContext, useEffect} from "react";
import {API} from "habit-core";

const NO_ORDER_EXECUTION_NAME = "Front of House Customer Display - Idle";
const ORDER_IN_PROGRESS_EXECUTION_NAME =
    "Front of House Customer Display - Active Order";
const LOAD_EXECUTIONS_INTERVAL_MS = 1800000; // 30 min in MS

type CustomerDisplayMediaContextType = {
    noOrderExecutionData: API.models.CampaignExecution | undefined;
    orderInProgressExecutionData: API.models.CampaignExecution | undefined;
};

export const CustomerDisplayMediaContext =
    createContext<CustomerDisplayMediaContextType>({
        noOrderExecutionData: undefined,
        orderInProgressExecutionData: undefined,
    });

export default function CustomerDisplayMediaProvider(props: {
    children: React.ReactNode;
}) {
    const [noOrderExecutionData, setNoOrderExecutionData] =
        useState<API.models.CampaignExecution>();
    const [orderInProgressExecutionData, setOrderInProgressExecutionData] =
        useState<API.models.CampaignExecution>();

    useEffect(() => {
        const loadExecutionData = () => {
            logger.info("Loading campaign execution data.");
            Promise.allSettled([
                marketing.getExecutionData(NO_ORDER_EXECUTION_NAME),
                marketing.getExecutionData(ORDER_IN_PROGRESS_EXECUTION_NAME),
            ]).then(
                ([
                    noOrderExecutionDataRes,
                    orderInProgressExecutionDataRes,
                ]) => {
                    if (noOrderExecutionDataRes.status === "fulfilled") {
                        setNoOrderExecutionData(noOrderExecutionDataRes.value);
                    } else {
                        logger.error(
                            "Failed to load no order execution data.",
                            {
                                errorMessage: noOrderExecutionDataRes.reason,
                            },
                        );
                    }
                    if (
                        orderInProgressExecutionDataRes.status === "fulfilled"
                    ) {
                        setOrderInProgressExecutionData(
                            orderInProgressExecutionDataRes.value,
                        );
                    } else {
                        logger.error(
                            "Failed to load order in progress execution data.",
                            {
                                errorMessage:
                                    orderInProgressExecutionDataRes.reason,
                            },
                        );
                    }
                },
            );
        };

        loadExecutionData();
        const loadExecutionInterval = setInterval(
            loadExecutionData,
            LOAD_EXECUTIONS_INTERVAL_MS,
        );
        return () => {
            clearInterval(loadExecutionInterval);
        };
    }, []);

    return (
        <CustomerDisplayMediaContext.Provider
            value={{
                noOrderExecutionData,
                orderInProgressExecutionData,
            }}>
            {props.children}
        </CustomerDisplayMediaContext.Provider>
    );
}
